import { OcppVersion } from '@/domain/types';
import type { OcppVersion as OcppVersionT, StationId } from '@/domain/types';

/**
 * Discriminated union of routing intents parsed from the URL. Source of
 * truth: data/url-parameters.yaml#boot_intents.
 *
 * Resolution against the Dexie registry happens in App.tsx (after parse).
 */
export type BootIntent =
  | { kind: 'registry'; mockCsms: boolean; tag?: string }
  | { kind: 'vehicles'; mockCsms: boolean; tag?: string }
  | { kind: 'openExistingStation'; stationId: StationId; mockCsms: boolean; tag?: string }
  | {
      kind: 'quickConfigByCpid';
      cpid: string;
      wsUrl: string | null;
      ocppVersion: OcppVersionT;
      mockCsms: boolean;
      tag?: string;
    };

const VERSION_BY_PARAM: Record<string, OcppVersionT> = {
  '1.6': OcppVersion.V16J,
  '1.6J': OcppVersion.V16J,
  '2.0.1': OcppVersion.V201,
  '2.1': OcppVersion.V21,
};

/**
 * Parse the current URL into an intent. evse2-compatible shortcuts:
 *   ?CPID=...&WSURL=...           -> quickConfigByCpid
 *   ?CPID=...                     -> quickConfigByCpid (wsUrl=null, resolved against registry)
 *   ?station=<uuid>               -> openExistingStation
 *   (no params)                   -> registry
 *
 * `?mockCsms=1` is test-only — it surfaces on every intent so main.tsx can
 * wire the in-process WS mock before mounting.
 */
export function parseBootIntent(url: URL): BootIntent {
  const mockCsms = url.searchParams.get('mockCsms') === '1';
  const tag = url.searchParams.get('TAG') ?? url.searchParams.get('tag') ?? undefined;
  const tagPart = tag !== undefined ? { tag } : {};

  const page = url.searchParams.get('page');
  if (page === 'vehicles') {
    return { kind: 'vehicles', mockCsms, ...tagPart };
  }

  const station = url.searchParams.get('station');
  if (station !== null && station.length > 0) {
    return {
      kind: 'openExistingStation',
      stationId: station as StationId,
      mockCsms,
      ...tagPart,
    };
  }

  const cpid = url.searchParams.get('CPID') ?? url.searchParams.get('cpid');
  if (cpid !== null && cpid.length > 0) {
    const wsUrl = url.searchParams.get('WSURL') ?? url.searchParams.get('wsUrl');
    const versionParam = url.searchParams.get('ocpp') ?? url.searchParams.get('OCPP') ?? '1.6J';
    const ocppVersion = VERSION_BY_PARAM[versionParam] ?? OcppVersion.V16J;
    return {
      kind: 'quickConfigByCpid',
      cpid,
      wsUrl: wsUrl !== null && wsUrl.length > 0 ? wsUrl : null,
      ocppVersion,
      mockCsms,
      ...tagPart,
    };
  }

  return { kind: 'registry', mockCsms, ...tagPart };
}
